/**
 * Clé de récupération (`docs/ARCHITECTURE.md` §4) : seul moyen de récupérer le
 * coffre si le mot de passe maître est perdu — sinon la perte est définitive,
 * c'est le prix du zéro-knowledge.
 *
 * ```
 * recoveryKey            = 128 bits du CSPRNG (a imprimer/garder par l'utilisateur)
 * recoveryStretchedKey   = HKDF-SHA256(recoveryKey, info="…|recovery-stretched-key")
 * recoveryProtectedKey   = seal(recoveryStretchedKey, vaultKey, contexte="…|recovery-protected-symmetric-key")
 * recoveryAuthProof      = HKDF-SHA256(recoveryKey, info="…|recovery-auth-proof")
 * ```
 *
 * `recoveryAuthProof` est au serveur ce que `masterPasswordHash` est à la
 * connexion : il en garde un re-hachage et l'exige pour réinitialiser le mot de
 * passe maître. Sans lui, connaître l'e-mail suffirait à remplacer les
 * identifiants d'un compte (prise de compte, effacement du coffre chiffré).
 *
 * Contrairement au mot de passe maître, la clé de récupération n'est PAS
 * étirée par Argon2id : ses 128 bits d'entropie CSPRNG rendent une attaque
 * hors ligne déjà impraticable, et le HKDF ne sert qu'à la séparation de
 * domaine et l'expansion 128→256 bits requise par l'AEAD.
 */
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { KDF_DOMAIN, RECOVERY_KEY_LENGTH, SEAL_CONTEXT, SYMMETRIC_KEY_LENGTH, type AeadAlgorithmId } from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import { base32ToBytes, bytesToBase32, bytesToBase64, utf8ToBytes, wipe } from './bytes.js';
import { InvalidParameterError } from './errors.js';
import { SecretKey, assertSecretKey } from './secret.js';

/** `vaultKey` scellée sous la clé de récupération. Seule forme que le serveur voit. */
export type RecoveryProtectedSymmetricKey = SealedString;

/** Génère une clé de récupération de 128 bits via le CSPRNG de la plateforme. */
export function generateRecoveryKey(): SecretKey {
  return SecretKey.random(RECOVERY_KEY_LENGTH, 'recoveryKey');
}

/**
 * Formate une clé de récupération pour affichage/impression : base32 groupé
 * par blocs de 4 caractères séparés par un tiret (ex. `A3F9-K2QW-…`).
 * Purement cosmétique — {@link parseRecoveryKey} accepte n'importe quel
 * groupage, casse ou espacement.
 */
export function formatRecoveryKey(recoveryKey: SecretKey): string {
  assertSecretKey(recoveryKey, 'recoveryKey', RECOVERY_KEY_LENGTH);
  const encoded = bytesToBase32(recoveryKey.reveal());
  return (encoded.match(/.{1,4}/gu) ?? [encoded]).join('-');
}

/**
 * Reconstruit la clé de récupération saisie par l'utilisateur, tolérante aux
 * espaces, tirets et à la casse (mêmes règles que {@link base32ToBytes}).
 *
 * @throws {InvalidParameterError} base32 invalide ou longueur inattendue
 */
export function parseRecoveryKey(formatted: string): SecretKey {
  const bytes = base32ToBytes(formatted);
  if (bytes.length !== RECOVERY_KEY_LENGTH) {
    wipe(bytes);
    throw new InvalidParameterError(
      `recoveryKey: ${RECOVERY_KEY_LENGTH} octets attendus, ${bytes.length} recus`,
    );
  }
  return SecretKey.adopt(bytes, 'recoveryKey', RECOVERY_KEY_LENGTH);
}

/**
 * `recoveryStretchedKey = HKDF-SHA256(recoveryKey, info="…|recovery-stretched-key")`.
 * Clé de scellement de la `vaultKey` par la voie de récupération.
 */
export function deriveRecoveryStretchedKey(recoveryKey: SecretKey): SecretKey {
  assertSecretKey(recoveryKey, 'recoveryKey', RECOVERY_KEY_LENGTH);
  const bytes = hkdf(
    sha256,
    recoveryKey.reveal(),
    undefined,
    utf8ToBytes(KDF_DOMAIN.recoveryStretchedKey),
    SYMMETRIC_KEY_LENGTH,
  );
  return SecretKey.adopt(bytes, 'recoveryStretchedKey', SYMMETRIC_KEY_LENGTH);
}

/**
 * `recoveryAuthProof = HKDF-SHA256(recoveryKey, info="…|recovery-auth-proof")`, en base64.
 * Domaine séparé de {@link deriveRecoveryStretchedKey} : la preuve ne révèle rien de la clé qui scelle la `vaultKey`.
 */
export function deriveRecoveryAuthProofBase64(recoveryKey: SecretKey): string {
  assertSecretKey(recoveryKey, 'recoveryKey', RECOVERY_KEY_LENGTH);
  const bytes = hkdf(sha256, recoveryKey.reveal(), undefined, utf8ToBytes(KDF_DOMAIN.recoveryAuthProof), SYMMETRIC_KEY_LENGTH);
  try {
    return bytesToBase64(bytes);
  } finally {
    wipe(bytes);
  }
}

export interface ProtectVaultKeyWithRecoveryKeyInput {
  readonly vaultKey: SecretKey;
  readonly recoveryKey: SecretKey;
  readonly algorithm?: AeadAlgorithmId;
}

/** Scelle la `vaultKey` sous la clé de récupération (nonce frais). */
export function protectVaultKeyWithRecoveryKey(
  input: ProtectVaultKeyWithRecoveryKeyInput,
): RecoveryProtectedSymmetricKey {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  assertSecretKey(input.recoveryKey, 'recoveryKey', RECOVERY_KEY_LENGTH);
  const stretched = deriveRecoveryStretchedKey(input.recoveryKey);
  try {
    return sealToString(stretched, input.vaultKey.reveal(), {
      context: SEAL_CONTEXT.recoveryProtectedSymmetricKey,
      algorithm: input.algorithm,
    });
  } finally {
    stretched.destroy();
  }
}

export interface UnprotectVaultKeyWithRecoveryKeyInput {
  readonly protectedKey: RecoveryProtectedSymmetricKey;
  readonly recoveryKey: SecretKey;
}

/**
 * Ouvre la `vaultKey` scellée sous la clé de récupération.
 *
 * @throws {DecryptionFailedError} clé de récupération incorrecte ou blob altéré
 * @throws {MalformedCiphertextError} conteneur structurellement invalide
 */
export function unprotectVaultKeyWithRecoveryKey(
  input: UnprotectVaultKeyWithRecoveryKeyInput,
): SecretKey {
  assertSecretKey(input?.recoveryKey, 'recoveryKey', RECOVERY_KEY_LENGTH);
  if (typeof input.protectedKey !== 'string' || input.protectedKey.length === 0) {
    throw new InvalidParameterError('protectedKey: recovery protected symmetric key manquante');
  }
  const stretched = deriveRecoveryStretchedKey(input.recoveryKey);
  try {
    const bytes = openFromString(stretched, input.protectedKey, {
      context: SEAL_CONTEXT.recoveryProtectedSymmetricKey,
    });
    if (bytes.length !== SYMMETRIC_KEY_LENGTH) {
      wipe(bytes);
      throw new InvalidParameterError('vaultKey dechiffree de longueur inattendue');
    }
    return SecretKey.adopt(bytes, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  } finally {
    stretched.destroy();
  }
}
